import {
  Business,
  CalendarToday,
  Group,
  ListAlt,
  MoreVert,
  Refresh,
  Search,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { Company } from "../../types";
import {
  LIST_SHARING_PUBLIC,
  LIST_SHARING_SHARED,
} from "../../constants";
import { hostname, parseError, User } from "../../utils";
import CreateListDialog from "./CreateListDialog";
import EditListNameDialog from "./EditListNameDialog";
import ListDetail from "./ListDetail";
import ListMetricsBar from "./ListMetricsBar";
import ListSharing from "./ListSharing";
import InfoAccordion from "../../components/InfoAccordion";

// Styled components for modern table look
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  overflowX: "auto",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  ...theme.applyStyles("dark", {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  }),
  "& .MuiTableCell-head": {
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    padding: theme.spacing(1.5, 2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    ...theme.applyStyles("dark", {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    }),
  },
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.action.hover, 0.02),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    ...theme.applyStyles("dark", {
      backgroundColor: alpha(theme.palette.action.hover, 0.04),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },
    }),
  },
  "& .MuiTableCell-body": {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const TypeBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: theme.shape.borderRadius * 4,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  fontSize: "0.75rem",
  fontWeight: 500,
  ...theme.applyStyles("dark", {
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
  }),
}));

export default ({
  mode,
  currentUser,
  getToken,
  eventListener,
  companyId,
  company,
  currentPlan,
  instanceId,
  openDialog,
}: {
  mode: string;
  getToken: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
  instanceId: string | undefined | null;
  openDialog: Function;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { whitelabelSettings } = useWhitelabel();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorList, setAnchorList] = useState<any>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [listSelected, setListSelected] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lists, setLists] = useState<Array<any>>([]);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [listSelectedSharing, setListSelectedSharing] = useState<any>(null);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [listSelectedEditName, setListSelectedEditName] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const isSearchTriggeredRef = useRef(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getData = async () => {
    setRefreshing(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/lists`, {
        params: { companyId, page: page + 1, pageSize, search: searchQuery },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setLists(res.data.items);
        setTotal(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const openList = (list: any) => {
    setListSelected(list);
    navigate(`${list.id}`);
  };

  const deleteList = async (listId: string, list: any) => {
    const currentToken = await getToken();
    openDialog(
      "Delete List",
      `Are you sure you want to delete this list ${list.name} and its data?`,
      "Confirm",
      () => {
        axios
          .delete(`${hostname}/lists/${listId}`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${currentToken}` },
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            getData();
          });
      }
    );
  };

  const openSharing = (list: any) => {
    setListSelectedSharing(list);
    setSharingOpen(true);
  };

  const openEditName = (list: any) => {
    setListSelectedEditName(list);
    setEditNameOpen(true);
  };

  useEffect(() => {
    if (currentUser.uid && !isSearchTriggeredRef.current) {
      getData();
    }
    // Reset the flag after handling
    isSearchTriggeredRef.current = false;
  }, [page]);

  useEffect(() => {
    if (currentUser.uid) {
      getData();
    }
  }, [currentUser.uid, companyId]);

  // Refresh list when returning from detail view (to update new row badges)
  useEffect(() => {
    if (currentUser.uid && !instanceId) {
      getData();
    }
  }, [instanceId]);

  useEffect(() => {
    if (currentUser.uid) {
      // Debounce the search to avoid too many requests
      const timeoutId = setTimeout(() => {
        isSearchTriggeredRef.current = true; // Set flag to prevent duplicate request
        setPage(0); // Reset to first page when searching
        getData();
      }, 500); // 500ms delay

      // Cleanup function to cancel the timeout if dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  return (
    <Box>
      {instanceId && (
        <ListDetail
          mode={mode}
          currentUser={currentUser}
          company={company}
          companyId={companyId}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          currentPlan={currentPlan}
          instanceId={instanceId}
          openDialog={
            openDialog as (
              title: string,
              content: string,
              buttonText: string,
              onClick: () => void
            ) => void
          }
        />
      )}
      <Box sx={{ display: instanceId ? "none" : "block" }}>
        <Box sx={{ mt: 2 }}>
          <ListMetricsBar
            companyId={companyId}
            getToken={getToken as () => Promise<string>}
          />
        </Box>
        <InfoAccordion title="Smart Lists Data Management" sx={{ mt: 2 }}>
          <Box component="ol" sx={{ mt: 1, pl: 2 }}>
            <li>
              Create structured lists to organize contacts, leads, or any data
            </li>
            <li>Import data from CSV files or add entries manually</li>
            <li>
              Set up automated actions (AI agents, HTTP requests, integrations)
            </li>
            <li>
              Use conditional logic to trigger actions based on data values
            </li>
            <li>Share lists with team members and configure webhooks</li>
          </Box>
          Smart Lists can feed data into Sequences, trigger workflows, and
          automate business processes.
        </InfoAccordion>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <Button
            data-tutorial="create-list"
            variant="contained"
            onClick={() => setCreateOpen(true)}
            startIcon={<ListAlt />}
            sx={{ flexShrink: 0 }}
          >
            New List
          </Button>
          <TextField
            placeholder="Search lists by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, maxWidth: isMobile ? "none" : 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Paper
          sx={{
            mt: 2.5,
            mb: 5,
            borderRadius: 3,
            overflow: "hidden",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
          elevation={0}
        >
          {refreshing && <LinearProgress sx={{ height: 3 }} />}
          {!refreshing && <Box sx={{ height: 3 }} />}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) =>
                alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {total} {total === 1 ? "list" : "lists"}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Refresh">
              <IconButton onClick={() => getData()} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <StyledTableContainer data-tutorial="list-grid">
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Sharing</TableCell>
                  <TableCell>Count</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Auto-Run</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {lists.map((list) => (
                  <StyledTableRow key={list.id} onClick={() => openList(list)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {list.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TypeBadge>
                        {list.type === "companies" ? (
                          <>
                            <Business sx={{ fontSize: 14 }} />
                            Companies
                          </>
                        ) : list.type === "custom" ? (
                          <>
                            <ListAlt sx={{ fontSize: 14 }} />
                            Custom
                          </>
                        ) : (
                          <>
                            <Group sx={{ fontSize: 14 }} />
                            Leads
                          </>
                        )}
                      </TypeBadge>
                    </TableCell>
                    <TableCell>
                      <Chip
                        variant={
                          list.sharing === LIST_SHARING_PUBLIC
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          list.sharing === LIST_SHARING_PUBLIC ||
                          list.sharing === LIST_SHARING_SHARED
                            ? "primary"
                            : "default"
                        }
                        label={list.sharing}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {list.count?.toLocaleString() || 0}
                        </Typography>
                        {list.newRowsSinceLastView > 0 && (
                          <Tooltip
                            title={`${list.newRowsSinceLastView} new row${
                              list.newRowsSinceLastView === 1 ? "" : "s"
                            } since last viewed`}
                          >
                            <Chip
                              label={`+${list.newRowsSinceLastView}`}
                              color="primary"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                fontWeight: "bold",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          list.created_date
                            ? moment(list.created_date).format(
                                "MMMM Do YYYY, h:mm a"
                              )
                            : ""
                        }
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <CalendarToday
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {list.created_date
                              ? moment(list.created_date).fromNow()
                              : "-"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {list.scheduledAutomations > 0 ? (
                        <Chip
                          label={`🤖 ${list.scheduledAutomations}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setAnchorList(list.id);
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={anchorList === list.id}
                        onClose={(e: any) => {
                          e.stopPropagation();
                          setAnchorEl(null);
                          setAnchorList(null);
                        }}
                        transformOrigin={{
                          horizontal: "right",
                          vertical: "top",
                        }}
                        anchorOrigin={{
                          horizontal: "right",
                          vertical: "bottom",
                        }}
                        slotProps={{
                          paper: {
                            elevation: 3,
                            sx: {
                              minWidth: 140,
                              borderRadius: 2,
                              mt: 0.5,
                            },
                          },
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            openList(list);
                            setAnchorEl(null);
                            setAnchorList(null);
                          }}
                        >
                          View
                        </MenuItem>
                        {list.uid === currentUser.uid && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditName(list);
                              setAnchorEl(null);
                              setAnchorList(null);
                            }}
                          >
                            Edit Name
                          </MenuItem>
                        )}
                        {list.uid === currentUser.uid && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openSharing(list);
                              setAnchorEl(null);
                              setAnchorList(null);
                            }}
                          >
                            Edit Sharing
                          </MenuItem>
                        )}
                        {list.uid === currentUser.uid && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteList(list.id, list);
                              setAnchorEl(null);
                              setAnchorList(null);
                            }}
                            sx={{ color: "error.main" }}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            />
          </StyledTableContainer>
        </Paper>
      </Box>
      <CreateListDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          getData();
        }}
        getToken={getToken}
        companyId={companyId}
        eventListener={eventListener}
      />
      <EditListNameDialog
        open={editNameOpen}
        onClose={() => {
          setEditNameOpen(false);
          setListSelectedEditName(null);
          getData();
        }}
        list={listSelectedEditName}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
      />
      <ListSharing
        open={sharingOpen}
        onClose={() => {
          setSharingOpen(false);
          setListSelectedSharing(null);
          getData();
        }}
        list={listSelectedSharing}
        getToken={getToken}
        eventListener={eventListener}
      />
    </Box>
  );
};
